import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { conditionalRender, valueRender } from '@repo/toolkit/conditional-render'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import DataTable, { Column } from '@repo/ui/data-table'
import Loader from '@repo/ui/loader'
import RadioInput from '@repo/ui/radio-input'
import SelectInput from '@repo/ui/select-input'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { getJourney } from '../../../../api/products/get-journey'
import { getJourneyTripPlaces } from '../../../../api/products/get-journey-trip-places'
import { getMainTripsByTripPlaceAndSecondaryTrip } from '../../../../api/products/get-main-trips-by-trip-place-and-secondary-trip'
import { getPlacesByTripPlaceAndSecondaryTripAndMainTrip } from '../../../../api/products/get-places-by-trip-place-and-secondary-trip-and-main-trip'
import { getSecondaryTripsByTripPlace } from '../../../../api/products/get-secondary-trips-by-trip-place'
import { EnrollmentPropositionInput } from '../../../../lib/types/models/enrollment-proposition/enrollment-proposition-input'
import { JourneyTripOptionLight } from '../../../../lib/types/models/product/journey'
import { TripProperties } from '../../../../lib/types/models/trip/properties.trip'
import { Trip, TripLight } from '../../../../lib/types/models/trip/trip'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import { FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledRadioInput from '../../../elements/inputs/controlled-radio-input'

type Props = {
  journeyId: number
  isLoading?: boolean
  onSubmit: () => void
  onCancel: () => void
}

enum TransportChoice {
  WITH_TRANSPORT = 'WITH_TRANSPORT',
  WITHOUT_TRANSPORT = 'WITHOUT_TRANSPORT'
}

const EnrollmentPropositionTrips = ({ journeyId, isLoading, onSubmit, onCancel }: Props) => {
  const form = useFormContext<EnrollmentPropositionInput>()
  const { journeyTripPlace, secondaryTrip, mainTrip, preferredPlace } = form.watch()
  const getTransportChoiceFromSelection = () => {
    if (
      mainTrip?.hasTransport === false ||
      (!!mainTrip && !!preferredPlace && !journeyTripPlace)
    ) {
      return TransportChoice.WITHOUT_TRANSPORT
    }
    return TransportChoice.WITH_TRANSPORT
  }
  const [transportChoice, setTransportChoice] = useState<TransportChoice>(
    getTransportChoiceFromSelection
  )

  const { data: journey, isPending } = useQuery({
    queryKey: ['journey', journeyId],
    queryFn: () => getJourney(journeyId)
  })

  const { data: tripPlaces } = useQuery({
    queryKey: ['journeyTripPlaces', journeyId],
    queryFn: () => getJourneyTripPlaces({ filter: { journeyId } })
  })

  const { data: secondaryTrips } = useQuery({
    queryKey: ['secondaryTrips', journeyId, journeyTripPlace?.id],
    queryFn: async () => {
      const res = await getSecondaryTripsByTripPlace({
        journeyId,
        tripPlaceId: journeyTripPlace?.id
      })
      if (res.length > 0) form.setValue('secondaryTrip', res[0])
      return res
    },
    enabled: transportChoice === TransportChoice.WITH_TRANSPORT && !!journeyTripPlace
  })

  const { data: mainTrips } = useQuery({
    queryKey: ['mainTrips', journeyId, journeyTripPlace, secondaryTrip?.id],
    queryFn: async () => {
      const res = await getMainTripsByTripPlaceAndSecondaryTrip({
        journeyId,
        tripPlaceId: journeyTripPlace?.id,
        secondaryTripId: secondaryTrip?.id
      })
      if (res.length === 1) form.setValue('mainTrip', res[0])
      return res
    },
    enabled:
      transportChoice === TransportChoice.WITH_TRANSPORT &&
      !!journeyTripPlace &&
      !!secondaryTrips &&
      (secondaryTrips.length === 0 || !!secondaryTrip)
  })

  const { data: places } = useQuery({
    queryKey: ['places', journeyId, journeyTripPlace?.id, secondaryTrip?.id, mainTrip?.id],
    queryFn: () => {
      if (journeyTripPlace && mainTrip)
        return getPlacesByTripPlaceAndSecondaryTripAndMainTrip({
          journeyId,
          secondaryTripId: secondaryTrip?.id,
          tripPlaceId: journeyTripPlace.id,
          mainTripId: mainTrip.id
        })
    },
    enabled: transportChoice === TransportChoice.WITH_TRANSPORT && !!journeyTripPlace && !!mainTrip
  })

  const noTransportOptions = useMemo(() => {
    const optionKeys = new Set<string>()

    return (
      journey?.places.filter((tripOption) => {
        const key = `${tripOption.mainTripId}-${tripOption.placeId}`
        const isCurrentSelection =
          tripOption.mainTripId === mainTrip?.id && tripOption.placeId === preferredPlace?.id
        if (
          (!tripOption.published && !isCurrentSelection) ||
          tripOption.mainTrip.hasTransport ||
          optionKeys.has(key)
        ) {
          return false
        }
        optionKeys.add(key)
        return true
      }) ?? []
    )
  }, [journey?.places, mainTrip?.id, preferredPlace?.id])
  const canChooseWithoutTransport = noTransportOptions.length > 0

  useEffect(() => {
    if (!mainTrip) return

    const nextTransportChoice = getTransportChoiceFromSelection()
    setTransportChoice(nextTransportChoice)

    if (
      nextTransportChoice === TransportChoice.WITHOUT_TRANSPORT &&
      mainTrip.hasTransport !== false
    ) {
      const selectedNoTransportOption = noTransportOptions.find(
        (option) => option.mainTripId === mainTrip.id && option.placeId === preferredPlace?.id
      )

      if (selectedNoTransportOption) form.setValue('mainTrip', selectedNoTransportOption.mainTrip)
    }
  }, [
    mainTrip?.id,
    mainTrip?.hasTransport,
    preferredPlace?.id,
    journeyTripPlace?.id,
    noTransportOptions,
    form
  ])

  useEffect(() => {
    if (
      transportChoice !== TransportChoice.WITHOUT_TRANSPORT ||
      mainTrip ||
      preferredPlace ||
      noTransportOptions.length !== 1
    ) {
      return
    }

    const [noTransportOption] = noTransportOptions
    form.setValue('mainTrip', noTransportOption.mainTrip)
    form.setValue('preferredPlace', noTransportOption.place)
    form.setValue('journeyTripPlace', undefined)
    form.setValue('secondaryTrip', null)
  }, [transportChoice, mainTrip, preferredPlace, noTransportOptions, form])

  // preselect the departure city when the journey offers only one
  useEffect(() => {
    if (
      transportChoice !== TransportChoice.WITH_TRANSPORT ||
      journeyTripPlace ||
      tripPlaces?.data.length !== 1
    ) {
      return
    }

    form.setValue('journeyTripPlace', tripPlaces.data[0])
  }, [transportChoice, journeyTripPlace, tripPlaces, form])

  const resetTripFields = () => {
    form.setValue('journeyTripPlace', undefined)
    form.setValue('secondaryTrip', null)
    form.setValue('mainTrip', undefined)
    form.setValue('preferredPlace', null)
  }

  // a stored "sans transport" place the stay no longer offers (another accomodation type) cannot
  // be kept: clear it so another place can be chosen
  useEffect(() => {
    if (
      !journey ||
      transportChoice !== TransportChoice.WITHOUT_TRANSPORT ||
      !mainTrip ||
      !preferredPlace
    )
      return
    const isOffered = noTransportOptions.some(
      (option) => option.mainTripId === mainTrip.id && option.placeId === preferredPlace.id
    )
    if (isOffered) return
    resetTripFields()
    if (!canChooseWithoutTransport) setTransportChoice(TransportChoice.WITH_TRANSPORT)
  }, [journey, transportChoice, mainTrip?.id, preferredPlace?.id, noTransportOptions])

  const getTripPriceValue = (trip?: TripLight): number | undefined => {
    const periodId = journey?.date.period?.id
    if (!trip || !periodId) return 0
    return (
      trip.prices?.find((price) => price.periods.some((period) => period.id === periodId))
        ?.price ?? 0
    )
  }

  const columns: Column<Trip>[] = [
    {
      accessorKey: 'id',
      header: '',
      cell: ({ row }) => {
        return (
          <FormField
            control={form.control}
            name={'mainTrip'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledRadioInput
                    {...field}
                    checked={mainTrip?.id === row.original.id}
                    onCheck={() => {
                      form.setValue('mainTrip', row.original)
                      if (row.original.id !== mainTrip?.id) form.setValue('preferredPlace', null)
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )
      }
    },
    {
      accessorKey: 'departure.label',
      header: 'Lieu de départ',
      enableSorting: true,
      cell: ({ row }) =>
        valueRender(row.original.departure, (departure) => getTranslatedName(departure))
    },
    {
      accessorKey: 'arrival.label',
      header: "Lieu d'arrivée",
      enableSorting: true,
      cell: ({ row }) => valueRender(row.original.arrival, (arrival) => getTranslatedName(arrival))
    },
    {
      accessorKey: 'transportMeans',
      header: 'Moyen de transport',
      enableSorting: true,
      cell: ({ row }) => TripProperties.handleTransportMeansName(row.original.transportMeans)
    },
    {
      accessorKey: 'price',
      header: 'Prix',
      enableSorting: true,
      cell: ({ row }) =>
        journey && (
          <Badge
            variant="primary"
            text={`${TripProperties.getTripPriceValue(journey, row.original)} €`}
          />
        )
    }
  ]

  const noTransportColumns: Column<JourneyTripOptionLight>[] = [
    {
      accessorKey: 'id',
      header: '',
      cell: ({ row }) => {
        const isSelected =
          mainTrip?.id === row.original.mainTrip.id && preferredPlace?.id === row.original.place.id

        return (
          <RadioInput
            checked={isSelected}
            onCheck={() => {
              form.setValue('mainTrip', row.original.mainTrip)
              form.setValue('preferredPlace', row.original.place)
              form.setValue('journeyTripPlace', undefined)
              form.setValue('secondaryTrip', null)
            }}
          />
        )
      }
    },
    {
      accessorKey: 'place.name',
      header: 'Lieu de séjour',
      enableSorting: true,
      cell: ({ row }) => row.original.place.name
    },

    {
      accessorKey: 'price',
      header: 'Prix',
      enableSorting: true,
      cell: ({ row }) => (
        <Badge variant="primary" text={`${getTripPriceValue(row.original.mainTrip)} €`} />
      )
    }
  ]

  if (!journey || isPending) return <Loader center />

  return (
    <div>
      <div className="h-8" />
      {canChooseWithoutTransport && (
        <div className="flex flex-col gap-2.5">
          <Typography variant="tag">Transport</Typography>
          <div className="flex flex-row gap-5">
            <RadioInput
              checked={transportChoice === TransportChoice.WITH_TRANSPORT}
              label="Avec transport"
              onCheck={() => {
                if (transportChoice === TransportChoice.WITH_TRANSPORT) return
                setTransportChoice(TransportChoice.WITH_TRANSPORT)
                resetTripFields()
              }}
            />
            <RadioInput
              checked={transportChoice === TransportChoice.WITHOUT_TRANSPORT}
              label="Sans transport"
              onCheck={() => {
                if (transportChoice === TransportChoice.WITHOUT_TRANSPORT) return
                setTransportChoice(TransportChoice.WITHOUT_TRANSPORT)
                resetTripFields()
              }}
            />
          </div>
        </div>
      )}
      <div className="h-3" />
      {transportChoice === TransportChoice.WITH_TRANSPORT && (
        <SelectInput
          required
          label="Ville de départ"
          options={tripPlaces?.data.map((tripPlace) => ({
            label: getTranslatedName(tripPlace),
            value: tripPlace.id
          }))}
          searchable
          dropDownPosition="above"
          selectedOption={conditionalRender(!!journeyTripPlace, {
            label: getTranslatedName(
              tripPlaces?.data.find((tripPlace) => tripPlace.id === journeyTripPlace?.id)
            ),
            value: journeyTripPlace?.id
          })}
          onChange={(value) => {
            const result = tripPlaces?.data.find((tripPlace) => tripPlace.id === value)
            form.setValue('journeyTripPlace', result)
            if (secondaryTrip) form.setValue('secondaryTrip', secondaryTrip)
            else form.setValue('secondaryTrip', null)
            if (value !== journeyTripPlace?.id) {
              form.setValue('secondaryTrip', null)
              form.setValue('mainTrip', undefined)
              form.setValue('preferredPlace', null)
            }
          }}
        />
      )}
      {transportChoice === TransportChoice.WITH_TRANSPORT && journeyTripPlace && (
        <div className="flex flex-col gap-3">
          {secondaryTrip && (
            <div className="flex gap-3 items-center">
              <Typography variant="tag">Voyage secondaire</Typography>
              <Badge
                text={TripProperties.getTripPrice(journey, secondaryTrip)}
                variant={'primary'}
              />
              {secondaryTrips && secondaryTrips.length > 1 && (
                <Typography variant={'small-medium'} color={tailwindColors.red.DEFAULT}>
                  Erreur: plusieurs voyages secondaires trouvés
                </Typography>
              )}
            </div>
          )}

          {mainTrips && mainTrips.length > 0 && (
            <div className={'flex flex-col gap-3'}>
              <Typography variant={'tag'}>Voyage principal</Typography>
              <DataTable<Trip>
                columns={columns}
                data={mainTrips}
                rowCount={mainTrips.length}
                paginated={false}
                disableHover
              />
            </div>
          )}

          {mainTrip && (
            <SelectInput
              optional
              label={'Préférence du lieu de séjour'}
              options={places?.map((place) => ({
                label: place.name,
                value: place.id
              }))}
              searchable
              selectedOption={conditionalRender(!!preferredPlace, {
                label: places?.find((place) => place.id === preferredPlace?.id)?.name || '',
                value: preferredPlace?.id
              })}
              onChange={(value) => {
                if (!value) {
                  form.setValue('preferredPlace', null)
                  return
                }
                form.setValue(
                  'preferredPlace',
                  places?.find((place) => place.id === value)
                )
              }}
            />
          )}
        </div>
      )}
      {canChooseWithoutTransport && transportChoice === TransportChoice.WITHOUT_TRANSPORT && (
        <div className="flex flex-col gap-3">
          <Typography variant={'tag'}>Lieu de séjour</Typography>
          <DataTable<JourneyTripOptionLight>
            columns={noTransportColumns}
            data={noTransportOptions}
            rowCount={noTransportOptions.length}
            paginated={false}
            disableHover
          />
        </div>
      )}
      <div className="h-6" />
      <div className="flex w-full justify-end mb-4 gap-3">
        <Button type="button" variant="ghost" onClick={onCancel} label="Retour" />
        <Button
          type="button"
          variant="primary"
          loading={isLoading}
          disabled={
            !mainTrip ||
            (transportChoice === TransportChoice.WITH_TRANSPORT && !journeyTripPlace) ||
            (transportChoice === TransportChoice.WITHOUT_TRANSPORT && !preferredPlace)
          }
          onClick={onSubmit}
          label="Valider"
        />
      </div>
    </div>
  )
}

export default EnrollmentPropositionTrips
